import { useCallback, useMemo } from 'react';
import { usePianoAnalytics } from '@sdx/b2o-fe-tools/pianoAnalytics';
import type {
  ClickActionProperties,
  PageDisplayProperties,
} from '@sdx/b2o-fe-tools/pianoAnalytics';

export interface BestEffortPiano {
  /** Fire a Piano `page.display`; a throwing tag is swallowed. */
  trackView: (properties: PageDisplayProperties) => void;
  /** Fire a Piano `click.action`; a throwing tag is swallowed. */
  trackClick: (properties: ClickActionProperties) => void;
}

/**
 * Best-effort Piano dispatch: `pageDisplay` / `clickAction` wrapped so a throwing
 * tag can never break the primary interaction it decorates (an OSAT submit /
 * dismiss, an Alert Bar action, …). The swallow policy was first introduced
 * inline in `useAlertBar` (#812085); it is centralised here (#826249) so every
 * Piano call site shares one place — the natural spot to add a breadcrumb if we
 * later want failed tags logged instead of vanishing silently.
 */
export const useBestEffortPiano = (): BestEffortPiano => {
  const { pageDisplay, clickAction } = usePianoAnalytics();

  const trackView = useCallback(
    (properties: PageDisplayProperties): void => {
      try {
        pageDisplay(properties);
      } catch {
        // Swallow — a failed tag must not break the interaction.
      }
    },
    [pageDisplay],
  );

  const trackClick = useCallback(
    (properties: ClickActionProperties): void => {
      try {
        clickAction(properties);
      } catch {
        // Swallow — a failed tag must not break the interaction.
      }
    },
    [clickAction],
  );

  // Memoise the returned object (not just the callbacks) so its identity is
  // stable across renders — mirrors usePianoAnalytics in fe-tools. Lets a
  // consumer safely put the whole hook result in a dependency array without
  // re-running the effect every render.
  return useMemo(() => ({ trackView, trackClick }), [trackView, trackClick]);
};
